/**
 * @description Company Settings Modal with inline-editable locations
 *
 * Key features:
 * - Company information form (existing CompanyForm)
 * - Inline-editable locations list (like Breaks in TimeClockSettings)
 * - View mode shows location summary
 * - Edit mode shows editable fields inline
 * - Compliance warnings for regulated states
 *
 * @dependencies
 * - CompanyForm: Company info edit form
 * - locationService: API calls for locations
 * - complianceStates: Labor law compliance checking
 * - CompanyForm Timezone is companies.timezone. Location TZ is not the clock.
 * - merchantAliasDeepLink.focusMerchantAliasField for Add Alias scroll
 *
 * @notes
 * - scrollToId="merchant-alias" runs after the dialog enter transition so the
 *   field exists in the Headless UI portal before we scroll/focus it.
 * - Time Clock / PTO live in LynxPay merchant global settings (/merchant/settings),
 *   not this modal. showMerchantGlobalSettings=false hides leftover links there.
 *
 * @see .claude/tasks/time-clock-employee-portal.md - Phase 10
 */

import React, { Fragment, useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { MapPin, Plus, Trash2, Edit2, Check, X } from 'lucide-react';
import CompanyForm from '../../shared/forms/CompanyForm';
import { hasComplianceRequirements, getComplianceMessage } from '../../../constants/complianceStates';
import locationService from '../../../services/api/locationService';
import PermissionGate from '../../shared/PermissionGate';
import { PERMISSIONS } from '../../../constants/permissionMappings';
import { focusMerchantAliasField, MERCHANT_ALIAS_ANCHOR_ID } from '../../../utils/merchantAliasDeepLink';
import { useAuth } from '../../../contexts/AuthContext';
import { usePackChromeIfPresent } from '../../../contexts/PackChromeContext';

/**
 * Company settings clock is companies.timezone from the org list.
 * Session /me attach is the invoice location clock — do not use it here.
 *
 * @param {{ id?: string, timezone?: string|null }|null|undefined} company
 * @param {Array<{ id?: string, timezone?: string|null }>|null|undefined} companies
 * @returns {string}
 */
function settingsTimezoneFromListed(company, companies) {
  const listed = (companies || []).find((row) => row.id === company?.id);
  if (listed && Object.prototype.hasOwnProperty.call(listed, 'timezone')) {
    return listed.timezone || '';
  }
  return '';
}

// US States dropdown
const US_STATES = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' }, { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' }, { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' }, { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' }, { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' }, { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' }, { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' }, { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' }, { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' }, { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' }, { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' }, { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' }, { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' }, { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' }, { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' }, { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' }, { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' }, { value: 'DC', label: 'Washington D.C.' }
];

// US Timezones
const US_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern (ET)' },
  { value: 'America/Chicago', label: 'Central (CT)' },
  { value: 'America/Denver', label: 'Mountain (MT)' },
  { value: 'America/Phoenix', label: 'Arizona (no DST)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { value: 'America/Anchorage', label: 'Alaska (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (HST)' },
];

// State to timezone mapping
const STATE_TIMEZONE_MAP = {
  'CA': 'America/Los_Angeles', 'WA': 'America/Los_Angeles', 'OR': 'America/Los_Angeles', 'NV': 'America/Los_Angeles',
  'AZ': 'America/Phoenix', 'CO': 'America/Denver', 'MT': 'America/Denver', 'NM': 'America/Denver',
  'UT': 'America/Denver', 'WY': 'America/Denver', 'ID': 'America/Denver',
  'TX': 'America/Chicago', 'IL': 'America/Chicago', 'MN': 'America/Chicago', 'WI': 'America/Chicago',
  'IA': 'America/Chicago', 'MO': 'America/Chicago', 'AR': 'America/Chicago', 'LA': 'America/Chicago',
  'MS': 'America/Chicago', 'AL': 'America/Chicago', 'OK': 'America/Chicago', 'KS': 'America/Chicago',
  'NE': 'America/Chicago', 'SD': 'America/Chicago', 'ND': 'America/Chicago',
  'NY': 'America/New_York', 'FL': 'America/New_York', 'PA': 'America/New_York', 'OH': 'America/New_York',
  'GA': 'America/New_York', 'NC': 'America/New_York', 'SC': 'America/New_York', 'VA': 'America/New_York',
  'WV': 'America/New_York', 'MD': 'America/New_York', 'DE': 'America/New_York', 'NJ': 'America/New_York',
  'CT': 'America/New_York', 'MA': 'America/New_York', 'RI': 'America/New_York', 'NH': 'America/New_York',
  'VT': 'America/New_York', 'ME': 'America/New_York', 'MI': 'America/New_York', 'IN': 'America/New_York',
  'KY': 'America/New_York', 'TN': 'America/Chicago', 'DC': 'America/New_York',
  'AK': 'America/Anchorage', 'HI': 'Pacific/Honolulu'
};

const CompanySettingsModal = ({
  isOpen,
  onClose,
  company,
  onUpdateCompany,
  isUpdating,
  updateError,
  scrollToId,
  showMerchantGlobalSettings = true
}) => {
  const { companies: authCompanies = [] } = useAuth() || {};
  const { towtechSoChrome } = usePackChromeIfPresent();
  const settingsInitialData = useMemo(() => ({
    ...(company || {}),
    timezone: settingsTimezoneFromListed(company, authCompanies)
  }), [company, authCompanies]);
  // Locations state
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [locationsError, setLocationsError] = useState(null);

  // Edit state - which location is being edited (null = none, 'new' = adding new)
  const [editingLocationId, setEditingLocationId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [savingLocation, setSavingLocation] = useState(false);
  const [locationSaveError, setLocationSaveError] = useState(null);

  // Unsaved changes tracking
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  // Handle close with unsaved changes check
  const handleClose = useCallback(() => {
    if (hasUnsavedChanges || editingLocationId) {
      setShowDiscardConfirm(true);
    } else {
      onClose();
    }
  }, [hasUnsavedChanges, editingLocationId, onClose]);

  // Confirm discard and close
  const handleConfirmDiscard = () => {
    setShowDiscardConfirm(false);
    setHasUnsavedChanges(false);
    onClose();
  };

  // Cancel discard - keep editing
  const handleCancelDiscard = () => {
    setShowDiscardConfirm(false);
  };

  // Fetch locations when modal opens
  const fetchLocations = useCallback(async () => {
    try {
      setLoadingLocations(true);
      setLocationsError(null);
      const response = await locationService.getLocations();
      setLocations(response.locations || []);
    } catch (err) {
      console.error('Failed to fetch locations:', err);
      setLocationsError('Failed to load locations');
    } finally {
      setLoadingLocations(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchLocations();
      setEditingLocationId(null);
      setEditFormData({});
      setLocationSaveError(null);
      setHasUnsavedChanges(false);
      setShowDiscardConfirm(false);
    }
  }, [isOpen, fetchLocations]);

  // Format address for display
  const formatAddress = (loc) => {
    const parts = [];
    if (loc.addressLine1) parts.push(loc.addressLine1);
    const cityStateZip = [loc.city, loc.state, loc.postalCode].filter(Boolean).join(', ');
    if (cityStateZip) parts.push(cityStateZip);
    return parts.join(', ') || 'No address';
  };

  // Format timezone for display
  const formatTimezone = (tz) => {
    const found = US_TIMEZONES.find(t => t.value === tz);
    return found?.label || tz?.split('/').pop()?.replace(/_/g, ' ') || 'Eastern';
  };

  // Start editing a location
  const startEditing = (location) => {
    setEditingLocationId(location.id);
    setEditFormData({
      name: location.name || '',
      addressLine1: location.addressLine1 || '',
      addressLine2: location.addressLine2 || '',
      city: location.city || '',
      state: location.state || '',
      postalCode: location.postalCode || '',
      timezone: location.timezone || 'America/New_York',
      isPrimary: location.isPrimary || false
    });
    setLocationSaveError(null);
  };

  // Start adding new location
  const startAddingLocation = () => {
    setEditingLocationId('new');
    setEditFormData({
      name: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      postalCode: '',
      timezone: 'America/New_York',
      isPrimary: locations.length === 0
    });
    setLocationSaveError(null);
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingLocationId(null);
    setEditFormData({});
    setLocationSaveError(null);
  };

  // Handle form field change
  const handleFieldChange = (field, value) => {
    setEditFormData(prev => {
      const updated = { ...prev, [field]: value };
      // Auto-suggest timezone when state changes
      if (field === 'state' && STATE_TIMEZONE_MAP[value]) {
        updated.timezone = STATE_TIMEZONE_MAP[value];
      }
      return updated;
    });
  };

  // Save location (create or update)
  const saveLocation = async () => {
    // Validate
    if (!editFormData.name?.trim()) {
      setLocationSaveError('Location name is required');
      return;
    }
    if (!editFormData.state) {
      setLocationSaveError('State is required for labor law compliance');
      return;
    }

    setSavingLocation(true);
    setLocationSaveError(null);

    try {
      if (editingLocationId === 'new') {
        await locationService.createLocation(editFormData);
      } else {
        await locationService.updateLocation(editingLocationId, editFormData);
      }
      await fetchLocations();
      cancelEditing();
    } catch (err) {
      console.error('Failed to save location:', err);
      setLocationSaveError(err.response?.data?.error || 'Failed to save location');
    } finally {
      setSavingLocation(false);
    }
  };

  // Delete location
  const deleteLocation = async (locationId) => {
    if (!window.confirm('Are you sure you want to delete this location?')) return;

    try {
      await locationService.deleteLocation(locationId);
      await fetchLocations();
    } catch (err) {
      console.error('Failed to delete location:', err);
      setLocationsError(err.response?.data?.error || 'Failed to delete location');
    }
  };

  // Render location in view mode
  const renderLocationView = (location) => {
    const hasCompliance = hasComplianceRequirements(location.state);
    const complianceMessage = getComplianceMessage(location.state);

    return (
      <div key={location.id} className="group p-4 bg-gray-50 rounded-xl border border-gray-100 hover:bg-gray-100/50 transition-colors">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="font-medium text-gray-900">{location.name}</span>
              {location.isPrimary && (
                <span className="text-[10px] font-bold uppercase tracking-wider bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded">
                  Primary
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600 ml-6">{formatAddress(location)}</p>
            <p className="text-xs text-gray-500 ml-6 mt-0.5">Timezone: {formatTimezone(location.timezone)}</p>

            {/* Compliance status */}
            <div className="ml-6 mt-2">
              {hasCompliance ? (
                <div className="flex items-center gap-1.5 text-amber-600">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span className="text-xs">{complianceMessage}</span>
                  {showMerchantGlobalSettings && (
                    <Link
                      to={`/merchant/settings?compliance=${location.state}&location=${encodeURIComponent(location.name)}&locationId=${location.id}`}
                      onClick={onClose}
                      className="text-xs font-medium text-amber-700 hover:text-amber-800 underline ml-1"
                    >
                      Configure
                    </Link>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-1.5 text-green-600">
                  <Check className="w-3.5 h-3.5" />
                  <span className="text-xs">Standard federal labor law</span>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <PermissionGate permission={PERMISSIONS.locations.edit}>
              <button
                onClick={() => startEditing(location)}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-colors"
                title="Edit location"
              >
                <Edit2 className="w-4 h-4" />
              </button>
            </PermissionGate>
            {!location.isPrimary && locations.length > 1 && (
              <PermissionGate permission={PERMISSIONS.locations.delete}>
                <button
                  onClick={() => deleteLocation(location.id)}
                  className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete location"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </PermissionGate>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render location in edit mode (inline form)
  const renderLocationEdit = (isNew = false) => {
    const hasCompliance = hasComplianceRequirements(editFormData.state);
    const complianceMessage = getComplianceMessage(editFormData.state);

    return (
      <div className="p-4 bg-yellow-50 rounded-xl border-2 border-yellow-200">
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm font-bold text-gray-900">
            {isNew ? 'New Location' : 'Editing Location'}
          </span>
          <button
            onClick={cancelEditing}
            className="p-1 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3">
          {/* Name */}
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">
              Location Name *
            </label>
            <input
              type="text"
              value={editFormData.name}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              placeholder="Main Office, Warehouse, etc."
              className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none"
            />
          </div>

          {/* Address */}
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">
              Address
            </label>
            <input
              type="text"
              value={editFormData.addressLine1}
              onChange={(e) => handleFieldChange('addressLine1', e.target.value)}
              placeholder="Street address"
              className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none mb-2"
            />
            <input
              type="text"
              value={editFormData.addressLine2}
              onChange={(e) => handleFieldChange('addressLine2', e.target.value)}
              placeholder="Suite, unit (optional)"
              className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none"
            />
          </div>

          {/* City, State, ZIP */}
          <div className="grid grid-cols-6 gap-2">
            <div className="col-span-3">
              <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">City</label>
              <input
                type="text"
                value={editFormData.city}
                onChange={(e) => handleFieldChange('city', e.target.value)}
                className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">State *</label>
              <select
                value={editFormData.state}
                onChange={(e) => handleFieldChange('state', e.target.value)}
                className="w-full px-2 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none"
              >
                <option value="">Select</option>
                {US_STATES.map(s => (
                  <option key={s.value} value={s.value}>{s.value}</option>
                ))}
              </select>
            </div>
            <div className="col-span-1">
              <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">ZIP</label>
              <input
                type="text"
                value={editFormData.postalCode}
                onChange={(e) => handleFieldChange('postalCode', e.target.value)}
                maxLength={10}
                className="w-full px-2 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none"
              />
            </div>
          </div>

          {/* Timezone */}
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Timezone</label>
            <select
              value={editFormData.timezone}
              onChange={(e) => handleFieldChange('timezone', e.target.value)}
              className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none"
            >
              {US_TIMEZONES.map(tz => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
          </div>

          {/* Primary checkbox */}
          {(locations.length > 0 || !isNew) && !editFormData.isPrimary && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={editFormData.isPrimary}
                onChange={(e) => handleFieldChange('isPrimary', e.target.checked)}
                className="w-4 h-4 text-brand border-gray-300 rounded focus:ring-brand"
              />
              <span className="text-sm text-gray-700">Set as primary location</span>
            </label>
          )}

          {/* Compliance warning */}
          {hasCompliance && (
            <div className="p-3 bg-amber-100 rounded-lg border border-amber-200">
              <div className="flex items-start gap-2">
                <svg className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <p className="text-sm text-amber-800">{complianceMessage}</p>
                  {showMerchantGlobalSettings && (
                    <p className="text-xs text-amber-700 mt-1">Configure compliance in Global Settings after saving.</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Error */}
          {locationSaveError && (
            <div className="p-2 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-red-700">{locationSaveError}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={cancelEditing}
              className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              disabled={savingLocation}
            >
              Cancel
            </button>
            <button
              onClick={saveLocation}
              disabled={savingLocation}
              className="px-3 py-1.5 text-sm font-medium bg-brand text-white rounded-lg hover:bg-brand-hover disabled:opacity-50 flex items-center gap-1.5"
            >
              {savingLocation && (
                <svg className="animate-spin h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {savingLocation ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              afterEnter={() => {
                if (scrollToId === MERCHANT_ALIAS_ANCHOR_ID) {
                  focusMerchantAliasField();
                }
              }}
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-xl bg-white text-left shadow-2xl transition-all w-full sm:my-8 sm:w-full sm:max-w-2xl">
                {/* Header */}
                <div className="bg-white px-6 py-5 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <Dialog.Title as="h3" className="text-xl font-semibold leading-6 text-gray-900">
                      Company Settings
                    </Dialog.Title>
                    <button
                      type="button"
                      className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2"
                      onClick={handleClose}
                      disabled={isUpdating || savingLocation}
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>
                </div>

                {/* Scrollable Content */}
                <div className="max-h-[calc(100vh-10rem)] overflow-y-auto">
                  {/* Company Information Form */}
                  <div className="px-6 py-4">
                    <CompanyForm
                      key={company?.id || 'new'}
                      onSubmit={onUpdateCompany}
                      initialData={settingsInitialData}
                      submitLabel="Save Changes"
                      loading={isUpdating}
                      error={updateError}
                      showTitle={false}
                      onDirtyChange={setHasUnsavedChanges}
                      requireEin={false}
                      requireTimezone={Boolean(towtechSoChrome)}
                      showMerchantAlias={Boolean(company?.elavonAssigned)}
                    />
                  </div>

                  {/* Locations Section */}
                  <div className="px-6 pb-6">
                    <div className="border-t border-gray-200 pt-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-5 h-5 text-gray-400" />
                          <h4 className="font-semibold text-gray-900">Locations</h4>
                        </div>
                        {editingLocationId !== 'new' && (
                          <PermissionGate permission={PERMISSIONS.locations.create}>
                            <button
                              onClick={startAddingLocation}
                              className="flex items-center gap-1.5 text-sm font-medium text-brand hover:text-brand-hover"
                            >
                              <Plus className="w-4 h-4" />
                              Add Location
                            </button>
                          </PermissionGate>
                        )}
                      </div>

                      {/* Error message */}
                      {locationsError && (
                        <div className="mb-4 p-3 bg-red-50 rounded-lg border border-red-200">
                          <p className="text-sm text-red-700">{locationsError}</p>
                        </div>
                      )}

                      {/* Loading */}
                      {loadingLocations ? (
                        <div className="py-8 text-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-brand mx-auto"></div>
                          <p className="text-sm text-gray-500 mt-2">Loading locations...</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {/* Existing locations */}
                          {locations.map(location => (
                            editingLocationId === location.id
                              ? <div key={location.id}>{renderLocationEdit(false)}</div>
                              : renderLocationView(location)
                          ))}

                          {/* New location form */}
                          {editingLocationId === 'new' && renderLocationEdit(true)}

                          {/* Empty state */}
                          {locations.length === 0 && editingLocationId !== 'new' && (
                            <div className="text-center py-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                              <MapPin className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                              <p className="text-sm font-medium text-gray-900">No locations yet</p>
                              <p className="text-xs text-gray-500 mt-1">Add your first location for labor law compliance</p>
                              <PermissionGate permission={PERMISSIONS.locations.create}>
                                <button
                                  onClick={startAddingLocation}
                                  className="mt-3 text-sm font-medium text-brand hover:text-brand-hover"
                                >
                                  + Add Location
                                </button>
                              </PermissionGate>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>

    {/* Discard Changes Confirmation Dialog */}
    <Transition.Root show={showDiscardConfirm} as={Fragment}>
        <Dialog as="div" className="relative z-[60]" onClose={handleCancelDiscard}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-xl bg-white text-left shadow-2xl transition-all w-full max-w-md p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                      <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900">
                        Unsaved Changes
                      </Dialog.Title>
                      <p className="mt-2 text-sm text-gray-600">
                        You have unsaved changes. Are you sure you want to close without saving?
                      </p>
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={handleCancelDiscard}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2"
                    >
                      Keep Editing
                    </button>
                    <button
                      type="button"
                      onClick={handleConfirmDiscard}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                    >
                      Discard Changes
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default CompanySettingsModal;
